import { DefaultSectionTitle } from '@components/Home/DefaultSectionTitle'
import { InView } from '@components/IntersectionObserver'
import { MediaRender } from '@components/MediaRender'
import { Slider } from '@components/Slider'
import { ExtraContentAllModal } from '@components/enrollments/courses/extra_contents/ExtraContentAllModal'
import { ExtraContentCard } from '@components/enrollments/courses/extra_contents/ExtraContentCard'
import { ExtraContentModal } from '@components/enrollments/courses/extra_contents/ExtraContentModal'
import { NotSupported } from '@components/enrollments/courses/extra_contents/NotSupported'
import { useExtraContentModalCard } from '@components/enrollments/courses/extra_contents/useExtraContentModalCard'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'
import { IExtraContent, ILessonsNodes, IMedia } from '@dtos/Course'
import { useCurrentMedia } from '@hooks/useCurrentMedia'
import { useEnrollment } from '@hooks/useEnrollment'
import { useExtraContents } from '@hooks/useExtraContents'
import { useLessonExtraContents } from '@hooks/useLessonExtraContents'
import { useGetMedia } from '@services/api/graphql/getMedia'
import { GestureReponderEvent } from '@tamagui/core'
import { isContentAvailable } from '@utils/contentAvailable'
import { courseExtraContentAvailability } from '@utils/courseExtraContentAvailability'
import {
  ILessonExtraContentAvailability,
  lessonExtraContentAvailability,
} from '@utils/lessonExtraContentAvailability'
import { openWebBrowser } from '@utils/openWebBrowser'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMedia, useWindowDimensions, YStack } from 'tamagui'

const LESSONS_PAGE_SIZE = 5

interface IExtraContentsSectionProps {
  title?: string | null
}

interface IExtraContentItem {
  key: string
  extraContent: IExtraContent
  availability?: ILessonExtraContentAvailability
}

/**
 * Seção "Materiais de apoio" da home focada (FR-018): os materiais gerais do
 * curso e os anexos das aulas liberadas numa fileira só, com o mesmo card, as
 * mesmas regras de liberação e download e a mesma visualização da tela de
 * material complementar. "Ver tudo" abre essa tela.
 */
export function ExtraContentsSection({ title }: IExtraContentsSectionProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [openedMedia, setOpenedMedia] = useState<IMedia | null>(null)
  const [isAllModalOpen, setIsAllModalOpen] = useState(false)
  const modalCard = useExtraContentModalCard()
  const [lessons, setLessons] = useState<ILessonsNodes[] | null>(null)
  const [hasMoreLessons, setHasMoreLessons] = useState(true)
  const isLoadingLessonsRef = useRef(false)
  const { t } = useTranslation('FeaturedProductHome')
  const { enrollment } = useEnrollment()
  const {
    extraContents,
    onLoadMore: loadMoreMainExtraContents,
    hasMore: hasMoreMainExtraContents,
  } = useExtraContents()
  const { lessonsExtraContents, onLoadMore: loadMoreLessonsExtraContents } =
    useLessonExtraContents()
  const { courseId, enrollmentId } = useCourseRouteParams()
  const { setCurrentMedia } = useCurrentMedia()
  const media = useMedia()
  const { width } = useWindowDimensions()

  const { data: mediaData, loading: isLoadingMedia } = useGetMedia({
    courseId: courseId?.toString() ?? '0',
    enrollmentId: enrollmentId?.toString() ?? '0',
    id: openedMedia?.id?.toString() ?? '0',
    enabled: !!openedMedia?.id,
  })

  useEffect(() => {
    if (lessons || !lessonsExtraContents) return
    setLessons(lessonsExtraContents.nodes)
  }, [lessons, lessonsExtraContents])

  useEffect(() => {
    setCurrentMedia({
      media: openedMedia,
      enrollmentId: Number(enrollmentId),
      courseId: Number(courseId),
    })
  }, [openedMedia, setCurrentMedia, enrollmentId, courseId])

  // Mesma largura do ExtraContentCard, para o slider calcular a espiada.
  const cardWidth = useMemo(() => {
    if (media.gtMd) return 407
    if (media.gtSm) return 324
    return 242
  }, [media.gtMd, media.gtSm])
  const containerPadding = media.gtSm ? 48 : 16
  const slidesSpacing = 24
  const slidesPerView = (width - containerPadding) / (cardWidth + slidesSpacing)

  const items = useMemo<IExtraContentItem[]>(() => {
    if (!enrollment) return []

    const mainItems = (extraContents?.nodes ?? []).map((extraContent) => ({
      key: `course-${extraContent.id}`,
      extraContent,
      availability: courseExtraContentAvailability(extraContent, enrollment),
    }))

    // Como no ExtraContentLessonSlider: anexo de aula só aparece com a aula
    // liberada, e herda as datas dela.
    const lessonItems = (lessons ?? []).flatMap((lesson) => {
      const lessonLiberationData = {
        releaseAt: lesson.releaseAt,
        releaseAfter: lesson.releaseAfter,
        lockAt: lesson.lockAt,
        lockAfter: lesson.lockAfter,
        createdAt: enrollment.activatedAt,
        courseClassRelease: enrollment.courseClassRelease,
        courseClassReleaseOffset: lesson.courseClassReleaseOffset,
      }
      if (!isContentAvailable(lessonLiberationData)) return []

      return lesson.extraMedias.map((extraMedia) => ({
        key: `lesson-${lesson.id}-${extraMedia.id}`,
        extraContent: {
          title: extraMedia.title,
          media: extraMedia,
          id: extraMedia.id,
          ...lessonLiberationData,
          downloadable: true,
        },
        availability: lessonExtraContentAvailability(
          lesson,
          extraMedia.id,
          enrollment,
        ),
      }))
    })

    return [...mainItems, ...lessonItems]
  }, [enrollment, extraContents, lessons])

  const handleLoadMoreLessons = useCallback(async () => {
    if (!lessons || isLoadingLessonsRef.current) return
    isLoadingLessonsRef.current = true
    try {
      const data = await loadMoreLessonsExtraContents?.(lessons.length)
      const page = data?.lessons?.nodes ?? []
      if (page.length < LESSONS_PAGE_SIZE) setHasMoreLessons(false)
      if (page.length === 0) return
      setLessons((prev) => {
        const existingIds = new Set((prev ?? []).map((lesson) => lesson.id))
        return [
          ...(prev ?? []),
          ...page.filter((lesson) => !existingIds.has(lesson.id)),
        ]
      })
    } finally {
      isLoadingLessonsRef.current = false
    }
  }, [lessons, loadMoreLessonsExtraContents])

  const handleLoadMore = useCallback(() => {
    if (hasMoreMainExtraContents) {
      loadMoreMainExtraContents?.(extraContents?.nodes.length ?? 0)
      return
    }
    if (hasMoreLessons) handleLoadMoreLessons()
  }, [
    hasMoreMainExtraContents,
    loadMoreMainExtraContents,
    extraContents?.nodes.length,
    hasMoreLessons,
    handleLoadMoreLessons,
  ])

  const handlePressDownload = useCallback(
    (e: GestureReponderEvent, link: string | null | undefined) => {
      if (link) openWebBrowser(link, true)
      e.stopPropagation()
    },
    [],
  )

  // Na home focada, "Ver tudo" abre o modal com os mesmos itens da seção, em
  // vez de levar à tela de material complementar.
  const handleSeeAll = useCallback(() => setIsAllModalOpen(true), [])
  const handleCloseAllModal = useCallback(() => setIsAllModalOpen(false), [])

  const allModalCards = useMemo(
    () =>
      items.map(({ key, extraContent, availability }, index) => {
        const card = (
          <ExtraContentCard
            key={key}
            extraContent={extraContent}
            isLessonAvailable
            availability={availability}
            handlePressDownload={handlePressDownload}
            handlePressOpenModal={() => {
              setIsAllModalOpen(false)
              setOpenedMedia(extraContent.media)
            }}
            withBorder
            {...modalCard}
          />
        )
        if (index !== items.length - 1) return card
        return (
          <InView
            key={key}
            onChange={(inView: boolean) => inView && handleLoadMore()}
            style={{ width: modalCard.widthValue }}
          >
            {card}
          </InView>
        )
      }),
    [items, handlePressDownload, modalCard, handleLoadMore],
  )

  const handleCloseModal = useCallback(() => setOpenedMedia(null), [])

  if (!enrollment || items.length === 0) return null

  const mediaType =
    openedMedia?.fileExt?.toLowerCase() === '.pdf'
      ? '.pdf'
      : openedMedia?.type || ''

  return (
    <YStack
      testID="featured-extra-contents-section"
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <Slider
        slidesTotalNumber={items.length}
        slidesPerView={slidesPerView}
        slidesSpacing={slidesSpacing}
        displaySliderControls={media.gtSm && items.length > slidesPerView}
        displaySliderControlsHorizontal={media.gtMd && isHovered}
        navigationControlVariation="dots"
        loadMoreContent={handleLoadMore}
        hasMore={hasMoreMainExtraContents || hasMoreLessons}
        contentInset={containerPadding}
        sliderTitle={
          <DefaultSectionTitle
            title={title || t('extraContents.defaultTitle')}
            onButtonPress={handleSeeAll}
            isHoveringSection={isHovered}
            showButton
          />
        }
      >
        {items.map(({ key, extraContent, availability }) => (
          <ExtraContentCard
            key={key}
            extraContent={extraContent}
            isLessonAvailable
            availability={availability}
            handlePressDownload={handlePressDownload}
            handlePressOpenModal={() => setOpenedMedia(extraContent.media)}
          />
        ))}
      </Slider>
      <ExtraContentAllModal
        title={title || t('extraContents.defaultTitle')}
        open={isAllModalOpen}
        onClose={handleCloseAllModal}
        cards={allModalCards}
      />
      <ExtraContentModal
        mediaType={mediaType}
        open={!!openedMedia}
        onClose={handleCloseModal}
        title={openedMedia?.title || ''}
        onChangeContent={() => null}
        disabledBtns={{ isFirstContent: true, isLastContent: true }}
        shouldAdapt={false}
      >
        {openedMedia && (
          <MediaRender
            media={openedMedia}
            closeModal={handleCloseModal}
            location="extraContent"
            fileUrl={mediaData?.media?.fileUrl || undefined}
            isLoading={isLoadingMedia}
          >
            <NotSupported
              media={openedMedia}
              fileUrl={mediaData?.media?.fileUrl}
            />
          </MediaRender>
        )}
      </ExtraContentModal>
    </YStack>
  )
}
